import * as React from "react";
import { Link } from "react-router-dom";
import {
  AlertCircle,
  ArrowRight,
  ClipboardList,
  IndianRupee,
  Loader2,
  MailX,
  MoreHorizontal,
  RefreshCw,
  Sparkles,
  TrendingUp,
  UserPlus2,
  Users2,
} from "lucide-react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  StatCard,
  type StatCardProps,
  toast,
} from "@eduviaos/ui-kit";
import { ApiError } from "@eduviaos/shared";
import type {
  RecentEnquiryItem,
  RecentStaffInviteItem,
  TenantDashboardSummary,
  TenantVerticalLiteral,
} from "@eduviaos/shared";
import { useAuth } from "@/auth/AuthProvider";
import { StudyCentreCharts } from "@/components/dashboard/StudyCentreCharts";
import { useCanManageAccounting } from "@/hooks/useAccountingAuthoring";
import { useDashboardSummary } from "@/hooks/useDashboardSummary";
import { formatPaiseCompact, useProfitLoss } from "@/lib/accounting/api";
import { useResendStaffInvite, useRevokeStaffInvite } from "@/hooks/useStaffInvites";
import { termsApiFor } from "@/hooks/useTerms";
import { greetingName } from "@/lib/greetingName";
import {
  getVerticalProfile,
  type VerticalProfile,
  type VerticalTerms,
} from "@/lib/verticalProfile";

const STAFF_ROLE_LABEL: Record<string, string> = {
  owner: "Owner",
  admin: "Admin",
  teacher: "Teacher",
  staff: "Staff",
};

/**
 * Phase D0c — Institution Admin dashboard
 * ----------------------------------------
 * Operational, vertical-aware. Replaces the previous capability-config
 * dashboard which leaked platform-internals (vertical defaults, branch
 * overrides) into the principal's daily view.
 *
 * Data source: `GET /me/dashboard/summary` (Phase D0b aggregator) —
 * single fetch returns everything the page needs.
 *
 * Layout:
 *   1. Header — greeting, tenant name, vertical badge, first-run CTA.
 *   2. Hero KPI row — vertical-aware (only renders cards for enabled
 *      modules whose data plane has shipped).
 *   3. Activity row (2-up) — recent admissions enquiries (left, 2/3)
 *      + pending staff invites (right, 1/3).
 * The centre owner does not see a module catalog or "live / coming
 * soon" status. Those are platform internals.
 *
 * Motion: card hovers are `transform 160ms ease-out` + `bg-color
 * 200ms`. No `transition: all`. Respects `prefers-reduced-motion`
 * via the StatCard primitive.
 */

const VERTICAL_LABEL: Record<string, string> = {
  school: "School",
  coaching_institute: "Coaching Institute",
  study_centre: "Study Centre",
};

const STAGE_TONE: Record<string, "info" | "secondary" | "warn" | "success"> = {
  enquiry: "info",
  application: "info",
  counselling: "info",
  test_scheduled: "warn",
  interview: "warn",
  admitted: "success",
  rejected: "secondary",
};

export function DashboardPage() {
  const { user } = useAuth();
  const summary = useDashboardSummary();
  const accounting = useCanManageAccounting();

  if (summary.isError) {
    return <DashboardErrorState message={summary.error?.message ?? "Unknown error"} />;
  }

  if (summary.isPending || !summary.data) {
    return <DashboardSkeleton />;
  }

  const data = summary.data;
  const verticalLabel = VERTICAL_LABEL[data.tenant.vertical] ?? data.tenant.vertical;
  // SC Foundation (F0b) — de-school the dashboard per the tenant's
  // vertical profile: a study centre counts "Learners" + a "Team"
  // (never students/teachers) and hides the disabled school-only module
  // cards that would otherwise be pure noise for a library owner.
  const profile = getVerticalProfile(data.tenant.vertical);
  const isStudyCentre = data.tenant.vertical === "study_centre";
  const showCentreFinances =
    isStudyCentre && data.enabled_modules.includes("accounting") && accounting.canViewAccounting;
  const showEnquiries = data.enabled_modules.includes("admissions");
  const showInvites = data.enabled_modules.includes("people");

  return (
    <div className="grid gap-6">
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="grid gap-1">
          <p className="text-muted-foreground text-sm">
            Signed in as <span className="text-foreground font-medium">{user?.email}</span>
          </p>
          <h1 className="text-3xl font-semibold tracking-tight">
            {greeting()}, {greetingName(user?.full_name ?? user?.email ?? "")}
          </h1>
          <p className="text-muted-foreground max-w-2xl text-sm">
            Here's what's happening at{" "}
            <span className="text-foreground font-medium">{data.tenant.name}</span>
            {!data.tenant.name.toLowerCase().includes(verticalLabel.toLowerCase()) && (
              <>
                {" "}
                <Badge variant="outline" className="ml-1 align-middle">
                  {verticalLabel}
                </Badge>
              </>
            )}
          </p>
        </div>
      </header>

      {/* ── Hero KPI row ─────────────────────────────────────────────── */}
      <section
        aria-label="At-a-glance metrics"
        className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4"
      >
        {buildKpiCards(data, profile, isStudyCentre).map((card) => (
          <StatCard key={card.label} {...card} />
        ))}
        {showCentreFinances ? <StudyCentreFinanceCards /> : null}
      </section>

      <StudyCentreCharts
        enabled={isStudyCentre}
        learningEnabled={data.enabled_modules.includes("learning")}
      />

      {/* ── Activity row: enquiries + staff invites ──────────────────── */}
      {showEnquiries || showInvites ? (
        <section className="grid gap-3 lg:grid-cols-3">
          {showEnquiries ? (
            <RecentEnquiriesCard items={data.recent.admissions_enquiries} terms={profile.terms} />
          ) : null}
          {showInvites ? (
            <PendingStaffInvitesCard
              items={data.recent.pending_staff_invites}
              isStudyCentre={isStudyCentre}
              vertical={data.tenant.vertical}
            />
          ) : null}
        </section>
      ) : null}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Card builders
// ─────────────────────────────────────────────────────────────────────────

function StudyCentreFinanceCards() {
  const now = new Date();
  const pnl = useProfitLoss(now.getFullYear(), now.getMonth() + 1);
  const income = pnl.data?.fee_income_paise;
  const profit = pnl.data?.net_profit_paise;
  if (
    pnl.isError &&
    pnl.error instanceof ApiError &&
    (pnl.error.status === 403 || pnl.error.status === 404)
  ) {
    return null;
  }
  return (
    <>
      <Link to="/accounting" className="block rounded-xl">
        <StatCard
          label="Revenue"
          value={pnl.isLoading || income == null ? "—" : formatPaiseCompact(income)}
          caption="Fee income this month"
          icon={<IndianRupee />}
          tone="green"
        />
      </Link>
      <Link to="/accounting" className="block rounded-xl">
        <StatCard
          label="Net profit"
          value={pnl.isLoading || profit == null ? "—" : formatPaiseCompact(profit)}
          caption="After team pay and expenses"
          icon={<TrendingUp />}
          tone={profit != null && profit < 0 ? "rose" : "amber"}
        />
      </Link>
    </>
  );
}

function buildKpiCards(
  data: TenantDashboardSummary,
  profile: VerticalProfile,
  isStudyCentre: boolean,
): StatCardProps[] {
  const cards: StatCardProps[] = [];
  const enabled = new Set(data.enabled_modules);

  if (enabled.has("people")) {
    const personMany = profile.personNoun.many;
    cards.push({
      label: personMany,
      value: formatNumber(data.kpis.students_total),
      caption:
        data.kpis.students_total === 0 ? `No ${personMany.toLowerCase()} yet` : "Active records",
      icon: <Users2 />,
      tone: "blue",
    });
    cards.push({
      label: isStudyCentre ? "Team" : "Staff",
      value: formatNumber(data.kpis.staff_total),
      caption:
        data.kpis.staff_total === 0
          ? isStudyCentre
            ? "Invite your first team member"
            : `Invite your first ${profile.terms.mentor.one.toLowerCase()}`
          : isStudyCentre
            ? "Your team"
            : `${profile.terms.mentor.many} + staff`,
      icon: <UserPlus2 />,
      tone: "violet",
    });
  }

  if (enabled.has("admissions")) {
    cards.push({
      label: "Open enquiries",
      value: formatNumber(data.kpis.admissions_open),
      caption: "Non-terminal stages",
      icon: <ClipboardList />,
      tone: "amber",
    });
    cards.push({
      label: "New this week",
      value: formatNumber(data.kpis.admissions_new_this_week),
      caption: "Enquiries in last 7 days",
      icon: <Sparkles />,
      tone: "green",
      delta:
        (data.kpis.admissions_new_this_week ?? 0) > 0
          ? { value: "Active funnel", trend: "up" }
          : { value: "Quiet week", trend: "flat" },
    });
  }

  if (cards.length === 0) {
    cards.push({
      label: isStudyCentre ? "Your centre" : "Your institution",
      value: "Ready",
      caption: "Day-to-day work lives in the menu",
      icon: <Sparkles />,
      tone: "blue",
    });
  }

  return cards;
}

function RecentEnquiriesCard(props: { items: RecentEnquiryItem[]; terms: VerticalTerms }) {
  const isEnquiryWording = props.terms.admission.one === "Enquiry";
  return (
    <Card className="lg:col-span-2">
      <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-3">
        <div>
          <CardTitle className="text-base">
            {isEnquiryWording ? "Recent enquiries" : "Recent admissions enquiries"}
          </CardTitle>
          <CardDescription>Newest open records — needs your attention.</CardDescription>
        </div>
        <Button asChild variant="ghost" size="sm">
          <Link to="/admissions">
            View all
            <ArrowRight aria-hidden="true" className="size-4" />
          </Link>
        </Button>
      </CardHeader>
      <CardContent>
        {props.items.length === 0 ? (
          <EmptyRow icon={<ClipboardList />} title="No open enquiries yet.">
            New enquiries will appear here as they come in. Add one manually from the{" "}
            {props.terms.admission.many} page.
          </EmptyRow>
        ) : (
          <ul className="divide-border/60 divide-y">
            {props.items.map((e) => (
              <EnquiryRow key={e.id} item={e} />
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function EnquiryRow({ item }: { item: RecentEnquiryItem }) {
  const tone = STAGE_TONE[item.stage_key] ?? "secondary";
  return (
    <li>
      <Link
        to={`/admissions/${item.id}`}
        className="hover:bg-muted/40 -mx-3 flex items-center gap-3 rounded-md px-3 py-2.5 transition-colors duration-150"
      >
        <div className="bg-primary/10 text-primary grid size-9 shrink-0 place-items-center rounded-lg">
          <ClipboardList className="size-4" aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">{item.student_name}</p>
          <p className="text-muted-foreground truncate text-xs">
            {item.student_email ?? item.student_phone ?? "No contact provided"}
          </p>
        </div>
        <div className="flex flex-col items-end gap-0.5">
          <Badge variant={tone} className="capitalize">
            {item.stage_key.replace(/_/g, " ")}
          </Badge>
          <span className="text-muted-foreground text-[11px]">{relativeTime(item.created_at)}</span>
        </div>
      </Link>
    </li>
  );
}

function PendingStaffInvitesCard(props: {
  items: RecentStaffInviteItem[];
  isStudyCentre?: boolean;
  vertical: TenantVerticalLiteral;
}) {
  const title = props.isStudyCentre ? "Pending team invites" : "Pending staff invites";
  // Phase E1d — bug #11. Surface resend + revoke per-row actions so the IA
  // can manage in-flight invites from the dashboard. Previously these only
  // existed inside the FirstRunInviteStaffPage wizard which the IA never
  // sees again after onboarding completes, so the two operations were
  // effectively missing from the product post-onboarding.
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">{title}</CardTitle>
        <CardDescription>Sent and awaiting acceptance.</CardDescription>
      </CardHeader>
      <CardContent>
        {props.items.length === 0 ? (
          <EmptyRow icon={<UserPlus2 />} title="No pending invites.">
            All your sent invites have been accepted (or none are pending).
          </EmptyRow>
        ) : (
          <ul className="grid grid-cols-[minmax(0,1fr)] gap-2.5">
            {props.items.map((invite) => (
              <PendingStaffInviteRow key={invite.id} invite={invite} vertical={props.vertical} />
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function PendingStaffInviteRow({
  invite,
  vertical,
}: {
  invite: RecentStaffInviteItem;
  vertical: TenantVerticalLiteral;
}) {
  const { roleLabel } = termsApiFor(vertical);
  const resend = useResendStaffInvite();
  const revoke = useRevokeStaffInvite();
  const [confirmRevokeOpen, setConfirmRevokeOpen] = React.useState(false);

  const isResending = resend.isPending;
  const isRevoking = revoke.isPending;
  const isBusy = isResending || isRevoking;

  // Phase E1.b.1 — Toast migration. The earlier (E1d) implementation
  // rendered an inline <p role="status"> banner under the row and self-
  // dismissed after 4s because the global toaster didn't exist yet. Now
  // that <Toaster /> is mounted in App.tsx we route through it instead:
  // the message is still announced (sonner injects an aria-live region)
  // but it doesn't push the row layout around, and a user clicking
  // Resend on the LAST visible row gets the same UX as the FIRST.
  const handleResend = () => {
    resend.mutate(invite.id, {
      onSuccess: (data) => {
        toast.success(
          data.invite.email_send_count
            ? `Invite re-sent to ${invite.email} (send #${data.invite.email_send_count}).`
            : `Invite re-sent to ${invite.email}.`,
        );
      },
      onError: (err) => {
        const apiErr = err instanceof ApiError ? err : null;
        const message =
          apiErr?.code === "INVITE_NOT_PENDING"
            ? "This invite was already accepted or revoked."
            : apiErr?.code === "NOT_FOUND"
              ? "This invite no longer exists."
              : (err.message ?? "Failed to resend invite.");
        toast.error(message);
      },
    });
  };

  const handleRevoke = () => {
    revoke.mutate(invite.id, {
      onSuccess: () => {
        toast.success(`Invite to ${invite.email} revoked.`);
        setConfirmRevokeOpen(false);
      },
      onError: (err) => {
        const apiErr = err instanceof ApiError ? err : null;
        const message =
          apiErr?.code === "NOT_FOUND"
            ? "This invite no longer exists."
            : (err.message ?? "Failed to revoke invite.");
        toast.error(message);
      },
    });
  };

  return (
    <li className="border-border bg-card grid min-w-0 gap-2 rounded-lg border px-3 py-2.5">
      <div className="flex items-center gap-3">
        <div className="grid size-9 shrink-0 place-items-center rounded-lg bg-amber-500/10 text-amber-700 dark:text-amber-400">
          <UserPlus2 className="size-4" aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">{invite.full_name}</p>
          <p className="text-muted-foreground truncate text-[11px]">{invite.email}</p>
        </div>
        <Badge variant="outline">
          {roleLabel(invite.role, STAFF_ROLE_LABEL[invite.role] ?? "Team member")}
        </Badge>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              aria-label={`Manage invite for ${invite.full_name}`}
              disabled={isBusy}
              className="size-8"
            >
              {isBusy ? (
                <Loader2 className="size-4 animate-spin" aria-hidden="true" />
              ) : (
                <MoreHorizontal className="size-4" aria-hidden="true" />
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={handleResend} disabled={isBusy}>
              <RefreshCw className="size-4" aria-hidden="true" />
              Resend invite
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={() => setConfirmRevokeOpen(true)}
              disabled={isBusy}
              className="text-destructive focus:text-destructive"
            >
              <MailX className="size-4" aria-hidden="true" />
              Revoke invite
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <Dialog open={confirmRevokeOpen} onOpenChange={setConfirmRevokeOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Revoke this invite?</DialogTitle>
            <DialogDescription>
              {invite.full_name} ({invite.email}) won&apos;t be able to accept the existing link.
              You can always send a fresh invite later.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setConfirmRevokeOpen(false)}
              disabled={isRevoking}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleRevoke} disabled={isRevoking}>
              {isRevoking ? (
                <>
                  <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                  Revoking…
                </>
              ) : (
                "Revoke invite"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </li>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Skeleton + error
// ─────────────────────────────────────────────────────────────────────────

function DashboardSkeleton() {
  return (
    <div className="grid gap-6">
      <div className="grid gap-2">
        <div className="bg-muted h-3 w-44 animate-pulse rounded" />
        <div className="bg-muted h-8 w-72 animate-pulse rounded" />
      </div>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            aria-busy="true"
            className="border-border bg-card grid h-[148px] animate-pulse gap-3 rounded-xl border p-5"
          >
            <div className="bg-muted h-4 w-24 rounded" />
            <div className="bg-muted h-9 w-32 rounded" />
            <div className="bg-muted h-3 w-40 rounded" />
          </div>
        ))}
      </div>
      <div className="grid gap-3 lg:grid-cols-3">
        <div className="bg-card border-border h-72 animate-pulse rounded-xl border lg:col-span-2" />
        <div className="bg-card border-border h-72 animate-pulse rounded-xl border" />
      </div>
    </div>
  );
}

function DashboardErrorState({ message }: { message: string }) {
  return (
    <div className="mx-auto max-w-xl py-16">
      <Card className="border-rose-500/30 bg-rose-500/5">
        <CardHeader className="flex-row items-start gap-3 space-y-0">
          <span className="grid size-10 shrink-0 place-items-center rounded-lg bg-rose-500/10 text-rose-600">
            <AlertCircle className="size-5" aria-hidden="true" />
          </span>
          <div>
            <CardTitle>Couldn't load your dashboard</CardTitle>
            <CardDescription className="mt-1">
              The aggregator returned an error. This usually clears up on retry.
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-muted-foreground font-mono text-xs">{message}</p>
          <Button onClick={() => window.location.reload()}>Reload</Button>
        </CardContent>
      </Card>
    </div>
  );
}

function EmptyRow({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-2 py-8 text-center">
      <span
        aria-hidden="true"
        className="bg-muted text-muted-foreground grid size-10 place-items-center rounded-full"
      >
        {icon}
      </span>
      <p className="text-foreground text-sm font-medium">{title}</p>
      {children ? <p className="text-muted-foreground max-w-sm text-xs">{children}</p> : null}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────

function greeting(): string {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

function formatNumber(n: number | null | undefined): number | string {
  if (n == null) return "—";
  return n;
}

function relativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.floor(ms / 60_000);
  if (min < 1) return "Just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  return new Date(iso).toLocaleDateString();
}
